/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismDefaultParameterId } from '@framework/cubismdefaultparameterid';
import { CubismModelSettingJson } from '@framework/cubismmodelsettingjson';
import {
  BreathParameterData,
  CubismBreath
} from '@framework/effect/cubismbreath';
import { LookParameterData, CubismLook } from '@framework/effect/cubismlook';
import { CubismEyeBlink } from '@framework/effect/cubismeyeblink';
import { ICubismModelSetting } from '@framework/icubismmodelsetting';
import { CubismIdHandle } from '@framework/id/cubismid';
import { CubismFramework } from '@framework/live2dcubismframework';
import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { CubismModel } from '@framework/model/cubismmodel';
import { CubismUserModel } from '@framework/model/cubismusermodel';
import {
  ACubismMotion,
  BeganMotionCallback,
  FinishedMotionCallback
} from '@framework/motion/acubismmotion';
import { CubismMotion } from '@framework/motion/cubismmotion';
import { CubismMotionManager } from '@framework/motion/cubismmotionmanager';
import {
  CubismMotionQueueEntryHandle,
  InvalidMotionQueueEntryHandleValue
} from '@framework/motion/cubismmotionqueuemanager';
import { CubismUpdateScheduler } from '@framework/motion/cubismupdatescheduler';
import { CubismBreathUpdater } from '@framework/motion/cubismbreathupdater';
import {
  CubismUpdateOrder,
  ICubismUpdater
} from '@framework/motion/icubismupdater';
import { CubismEyeBlinkUpdater } from '@framework/motion/cubismeyeblinkupdater';
import { CubismExpressionUpdater } from '@framework/motion/cubismexpressionupdater';
import { CubismPhysicsUpdater } from '@framework/motion/cubismphysicsupdater';
import { CubismPoseUpdater } from '@framework/motion/cubismposeupdater';
import { CubismLipSyncUpdater } from '@framework/motion/cubismlipsyncupdater';
import { csmRect } from '@framework/type/csmrectf';
import {
  CSM_ASSERT,
  CubismLogError,
  CubismLogInfo
} from '@framework/utils/cubismdebug';

import * as LAppDefine from './lappdefine';
import { LAppPal } from './lapppal';
import { TextureInfo } from './lapptexturemanager';
import { LAppWavFileHandler } from './lappwavfilehandler';
import { CubismMoc } from '@framework/model/cubismmoc';
import { LAppDelegate } from './lappdelegate';
import { LAppSubdelegate } from './lappsubdelegate';
import type { FollowConfig } from './generated_model_types';

export type HiddenModelGroup = {
  partIds?: string[];
  drawableIds?: string[];
  matchPrefixes?: string[];
};

type HiddenTargetSet = {
  partIndices: Set<number>;
  drawableIndices: Set<number>;
};

type HiddenPartState = {
  opacity: number;
};

type HiddenDrawableState = {
  opacity: number;
  culling: boolean;
  overridden: boolean;
};

export type DrawableBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  drawableCount: number;
  vertexCount: number;
  coordinateSpace: 'model' | 'model_matrix';
};

type SmoothDampResult = {
  value: number;
  velocity: number;
};

const defaultFollowDamping = 0.15;
const followLookAmplitude = 2.0;
const followRestEpsilon = 0.0001;

function smoothDamp(
  current: number,
  target: number,
  velocity: number,
  smoothTime: number,
  deltaTime: number
): SmoothDampResult {
  const safeSmoothTime = Math.max(0.0001, smoothTime);
  const omega = 2.0 / safeSmoothTime;
  const x = omega * Math.max(0, deltaTime);
  const exp = 1.0 / (1.0 + x + 0.48 * x * x + 0.235 * x * x * x);
  const change = current - target;
  const temp = (velocity + omega * change) * deltaTime;
  const nextVelocity = (velocity - omega * temp) * exp;
  const nextValue = target + (change + temp) * exp;

  if ((target - current > 0) === (nextValue > target)) {
    return { value: target, velocity: 0 };
  }

  return { value: nextValue, velocity: nextVelocity };
}

class ConfigurableLookUpdater extends ICubismUpdater {
  public constructor(
    private readonly updateCallback: (
      model: CubismModel,
      deltaTimeSeconds: number
    ) => void
  ) {
    super(CubismUpdateOrder.CubismUpdateOrder_Drag);
  }

  public onLateUpdate(
    model: CubismModel,
    deltaTimeSeconds: number
  ): void {
    this.updateCallback(model, deltaTimeSeconds);
  }
}

enum LoadStep {
  LoadAssets,
  LoadModel,
  WaitLoadModel,
  LoadExpression,
  WaitLoadExpression,
  LoadPhysics,
  WaitLoadPhysics,
  LoadPose,
  WaitLoadPose,
  SetupEyeBlink,
  SetupBreath,
  LoadUserData,
  WaitLoadUserData,
  SetupEyeBlinkIds,
  SetupLipSyncIds,
  SetupLook,
  SetupLayout,
  LoadMotion,
  WaitLoadMotion,
  CompleteInitialize,
  CompleteSetupModel,
  LoadTexture,
  WaitLoadTexture,
  CompleteSetup
}

/**
 * ユーザーが実際に使用するモデルの実装クラス<br>
 * モデル生成、機能コンポーネント生成、更新処理とレンダリングの呼び出しを行う。
 */
export class LAppModel extends CubismUserModel {
  /**
   * model3.jsonが置かれたディレクトリとファイルパスからモデルを生成する
   * @param dir
   * @param fileName
   */
  public loadAssets(dir: string, fileName: string): void {
    this._modelHomeDir = dir;

    fetch(`${this._modelHomeDir}${fileName}`)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => {
        const setting: ICubismModelSetting = new CubismModelSettingJson(
          arrayBuffer,
          arrayBuffer.byteLength
        );

        // ステートを更新
        this._state = LoadStep.LoadModel;

        // 結果を保存
        this.setupModel(setting);
      })
      .catch(error => {
        // model3.json読み込みでエラーが発生した時点で描画は不可能なので、setupせずエラーをcatchして何もしない
        CubismLogError(`Failed to load file ${this._modelHomeDir}${fileName}`);
      });
  }

  /**
   * model3.jsonからモデルを生成する。
   * model3.jsonの記述に従ってモデル生成、モーション、物理演算などのコンポーネント生成を行う。
   *
   * @param setting ICubismModelSettingのインスタンス
   */
  private setupModel(setting: ICubismModelSetting): void {
    this._updating = true;
    this._initialized = false;

    this._modelSetting = setting;

    // CubismModel
    if (this._modelSetting.getModelFileName() != '') {
      const modelFileName = this._modelSetting.getModelFileName();

      fetch(`${this._modelHomeDir}${modelFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${modelFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          this.loadModel(arrayBuffer, this._mocConsistency);
          this._state = LoadStep.LoadExpression;

          // callback
          loadCubismExpression();
        });

      this._state = LoadStep.WaitLoadModel;
    } else {
      LAppPal.printMessage('Model data does not exist.');
    }

    // Expression
    const loadCubismExpression = (): void => {
      if (this._modelSetting.getExpressionCount() > 0) {
        const count: number = this._modelSetting.getExpressionCount();

        for (let i = 0; i < count; i++) {
          const expressionName = this._modelSetting.getExpressionName(i);
          const expressionFileName =
            this._modelSetting.getExpressionFileName(i);

          fetch(`${this._modelHomeDir}${expressionFileName}`)
            .then(response => {
              if (response.ok) {
                return response.arrayBuffer();
              } else if (response.status >= 400) {
                CubismLogError(
                  `Failed to load file ${this._modelHomeDir}${expressionFileName}`
                );
                // ファイルが存在しなくてもresponseはnullを返却しないため、空のArrayBufferで対応する
                return new ArrayBuffer(0);
              }
            })
            .then(arrayBuffer => {
              const motion: ACubismMotion = this.loadExpression(
                arrayBuffer,
                arrayBuffer.byteLength,
                expressionName
              );

              if (this._expressions.get(expressionName) != null) {
                ACubismMotion.delete(this._expressions.get(expressionName));
                this._expressions.set(expressionName, null);
              }

              this._expressions.set(expressionName, motion);

              this._expressionCount++;

              if (this._expressionCount >= count) {
                // Expression Updaterの追加
                if (this._expressionManager != null) {
                  const expressionUpdater = new CubismExpressionUpdater(
                    this._expressionManager
                  );
                  this._updateScheduler.addUpdatableList(expressionUpdater);
                }

                this._state = LoadStep.LoadPhysics;

                // callback
                loadCubismPhysics();
              }
            });
        }
        this._state = LoadStep.WaitLoadExpression;
      } else {
        this._state = LoadStep.LoadPhysics;

        // callback
        loadCubismPhysics();
      }
    };

    // Physics
    const loadCubismPhysics = (): void => {
      if (this._modelSetting.getPhysicsFileName() != '') {
        const physicsFileName = this._modelSetting.getPhysicsFileName();

        fetch(`${this._modelHomeDir}${physicsFileName}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${physicsFileName}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadPhysics(arrayBuffer, arrayBuffer.byteLength);

            // Physics Updaterの追加
            if (this._physics) {
              const physicsUpdater = new CubismPhysicsUpdater(this._physics);
              this._updateScheduler.addUpdatableList(physicsUpdater);
            }

            this._state = LoadStep.LoadPose;

            // callback
            loadCubismPose();
          });
        this._state = LoadStep.WaitLoadPhysics;
      } else {
        this._state = LoadStep.LoadPose;

        // callback
        loadCubismPose();
      }
    };

    // Pose
    const loadCubismPose = (): void => {
      if (this._modelSetting.getPoseFileName() != '') {
        const poseFileName = this._modelSetting.getPoseFileName();

        fetch(`${this._modelHomeDir}${poseFileName}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${poseFileName}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadPose(arrayBuffer, arrayBuffer.byteLength);

            // Pose Updaterの追加
            if (this._pose) {
              const poseUpdater = new CubismPoseUpdater(this._pose);
              this._updateScheduler.addUpdatableList(poseUpdater);
            }

            this._state = LoadStep.SetupEyeBlink;

            // callback
            setupEyeBlink();
          });
        this._state = LoadStep.WaitLoadPose;
      } else {
        this._state = LoadStep.SetupEyeBlink;

        // callback
        setupEyeBlink();
      }
    };

    // EyeBlink
    const setupEyeBlink = (): void => {
      if (this._modelSetting.getEyeBlinkParameterCount() > 0) {
        this._eyeBlink = CubismEyeBlink.create(this._modelSetting);
        const eyeBlinkUpdater = new CubismEyeBlinkUpdater(
          () => this._motionUpdated,
          this._eyeBlink
        );
        this._updateScheduler.addUpdatableList(eyeBlinkUpdater);
      }

      this._state = LoadStep.SetupBreath;

      // callback
      setupBreath();
    };

    // Breath
    const setupBreath = (): void => {
      this._breath = CubismBreath.create();

      const breathParameters: Array<BreathParameterData> = [
        new BreathParameterData(this._idParamAngleX, 0.0, 15.0, 6.5345, 0.5),
        new BreathParameterData(this._idParamAngleY, 0.0, 8.0, 3.5345, 0.5),
        new BreathParameterData(this._idParamAngleZ, 0.0, 10.0, 5.5345, 0.5),
        new BreathParameterData(
          this._idParamBodyAngleX,
          0.0,
          4.0,
          15.5345,
          0.5
        ),
        new BreathParameterData(
          CubismFramework.getIdManager().getId(
            CubismDefaultParameterId.ParamBreath
          ),
          0.5,
          0.5,
          3.2345,
          1
        )
      ];

      this._breath.setParameters(breathParameters);

      const breathUpdater = new CubismBreathUpdater(this._breath);
      this._updateScheduler.addUpdatableList(breathUpdater);

      this._state = LoadStep.LoadUserData;

      // callback
      loadUserData();
    };

    // UserData
    const loadUserData = (): void => {
      if (this._modelSetting.getUserDataFile() != '') {
        const userDataFile = this._modelSetting.getUserDataFile();

        fetch(`${this._modelHomeDir}${userDataFile}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${userDataFile}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadUserData(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.SetupEyeBlinkIds;

            // callback
            setupEyeBlinkIds();
          });

        this._state = LoadStep.WaitLoadUserData;
      } else {
        this._state = LoadStep.SetupEyeBlinkIds;

        // callback
        setupEyeBlinkIds();
      }
    };

    // EyeBlinkIds
    const setupEyeBlinkIds = (): void => {
      const eyeBlinkIdCount: number =
        this._modelSetting.getEyeBlinkParameterCount();

      this._eyeBlinkIds.length = eyeBlinkIdCount;
      for (let i = 0; i < eyeBlinkIdCount; ++i) {
        this._eyeBlinkIds[i] = this._modelSetting.getEyeBlinkParameterId(i);
      }

      this._state = LoadStep.SetupLipSyncIds;

      // callback
      setupLipSyncIds();
    };

    // LipSyncIds
    const setupLipSyncIds = (): void => {
      const lipSyncIdCount = this._modelSetting.getLipSyncParameterCount();

      this._lipSyncIds.length = lipSyncIdCount;
      for (let i = 0; i < lipSyncIdCount; ++i) {
        this._lipSyncIds[i] = this._modelSetting.getLipSyncParameterId(i);
      }

      // LipSync Updaterの追加
      if (this._lipSyncIds.length > 0) {
        const lipSyncUpdater = new CubismLipSyncUpdater(
          this._lipSyncIds,
          this._wavFileHandler
        );
        this._updateScheduler.addUpdatableList(lipSyncUpdater);
      }

      this._state = LoadStep.SetupLook;

      // callback
      setupLook();
    };

    // Look
    const setupLook = (): void => {
      this._look = CubismLook.create();

      const lookParameters: Array<LookParameterData> = [
        new LookParameterData(this._idParamAngleX, 30.0, 0.0, 0.0),
        new LookParameterData(this._idParamAngleY, 0.0, 30.0, 0.0),
        new LookParameterData(this._idParamAngleZ, 0.0, 0.0, -30.0),
        new LookParameterData(this._idParamBodyAngleX, 10.0, 0.0, 0.0),
        new LookParameterData(
          CubismFramework.getIdManager().getId(
            CubismDefaultParameterId.ParamEyeBallX
          ),
          1.0,
          0.0,
          0.0
        ),
        new LookParameterData(
          CubismFramework.getIdManager().getId(
            CubismDefaultParameterId.ParamEyeBallY
          ),
          0.0,
          1.0,
          0.0
        )
      ];

      this._defaultLookParameters = lookParameters;
      this._look.setParameters(this._defaultLookParameters);

      const lookUpdater = new ConfigurableLookUpdater(
        (model, deltaTimeSeconds) => {
          this.updateLookParameters(model, deltaTimeSeconds);
        }
      );
      this._updateScheduler.addUpdatableList(lookUpdater);

      // callback
      finalizeUpdaters();
    };

    // UpdateScheduler最終化処理
    const finalizeUpdaters = (): void => {
      // 全てのUpdaterが追加されたのでUpdateSchedulerを最終ソート
      this._updateScheduler.sortUpdatableList();

      this._state = LoadStep.SetupLayout;

      // callback
      setupLayout();
    };

    // Layout
    const setupLayout = (): void => {
      const layout: Map<string, number> = new Map<string, number>();

      if (this._modelSetting == null || this._modelMatrix == null) {
        CubismLogError('Failed to setupLayout().');
        return;
      }

      this._modelSetting.getLayoutMap(layout);
      this._modelMatrix.setupFromLayout(layout);
      this._state = LoadStep.LoadMotion;

      // callback
      loadCubismMotion();
    };

    // Motion
    const loadCubismMotion = (): void => {
      this._state = LoadStep.WaitLoadMotion;
      this._model.saveParameters();
      this._allMotionCount = 0;
      this._motionCount = 0;
      const group: string[] = [];

      const motionGroupCount: number = this._modelSetting.getMotionGroupCount();

      // モーションの総数を求める
      for (let i = 0; i < motionGroupCount; i++) {
        group[i] = this._modelSetting.getMotionGroupName(i);
        this._allMotionCount += this._modelSetting.getMotionCount(group[i]);
      }

      // モーションの読み込み
      for (let i = 0; i < motionGroupCount; i++) {
        this.preLoadMotionGroup(group[i]);
      }

      // モーションがない場合
      if (motionGroupCount == 0) {
        this._state = LoadStep.LoadTexture;

        // 全てのモーションを停止する
        this._motionManager.stopAllMotions();

        this._updating = false;
        this._initialized = true;

        this.createRenderer(
          this._subdelegate.getCanvas().width,
          this._subdelegate.getCanvas().height
        );
        this.setupTextures();
        this.getRenderer().startUp(this._subdelegate.getGlManager().getGl());
        this.getRenderer().loadShaders(LAppDefine.ShaderPath);
      }
    };
  }

  /**
   * テクスチャユニットにテクスチャをロードする
   */
  private setupTextures(): void {
    // iPhoneでのアルファ品質向上のためTypescriptではpremultipliedAlphaを採用
    const usePremultiply = true;

    if (this._state == LoadStep.LoadTexture) {
      // テクスチャ読み込み用
      const textureCount: number = this._modelSetting.getTextureCount();

      for (
        let modelTextureNumber = 0;
        modelTextureNumber < textureCount;
        modelTextureNumber++
      ) {
        // テクスチャ名が空文字だった場合はロード・バインド処理をスキップ
        if (this._modelSetting.getTextureFileName(modelTextureNumber) == '') {
          console.log('getTextureFileName null');
          continue;
        }

        // WebGLのテクスチャユニットにテクスチャをロードする
        let texturePath =
          this._modelSetting.getTextureFileName(modelTextureNumber);
        texturePath = this._modelHomeDir + texturePath;

        // ロード完了時に呼び出すコールバック関数
        const onLoad = (textureInfo: TextureInfo): void => {
          this.getRenderer().bindTexture(modelTextureNumber, textureInfo.id);

          this._textureCount++;

          if (this._textureCount >= textureCount) {
            // ロード完了
            this._state = LoadStep.CompleteSetup;
            this.startInitialIdleMotion();
          }
        };

        // 読み込み
        this._subdelegate
          .getTextureManager()
          .createTextureFromPngFile(texturePath, usePremultiply, onLoad);
        this.getRenderer().setIsPremultipliedAlpha(usePremultiply);
      }

      this._state = LoadStep.WaitLoadTexture;
    }
  }

  /**
   * レンダラを再構築する
   */
  public reloadRenderer(): void {
    this.deleteRenderer();
    this.createRenderer(
      this._subdelegate.getCanvas().width,
      this._subdelegate.getCanvas().height
    );
    this.setupTextures();
  }

  /**
   * 更新
   */
  public setHiddenGroups(groups: HiddenModelGroup[]): void {
    this._hiddenGroups = Array.isArray(groups) ? groups : [];
    this.applyHiddenGroups();
  }

  public getCurrentDrawableBounds(useModelMatrix: boolean = false): DrawableBounds | null {
    if (!this._model || this._state != LoadStep.CompleteSetup) {
      return null;
    }

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let visibleDrawableCount = 0;
    let visibleVertexCount = 0;
    const { drawableOpacities } = this.getCoreOpacityArrays();
    const drawableCount = this._model.getDrawableCount();

    for (let drawableIndex = 0; drawableIndex < drawableCount; drawableIndex++) {
      const opacity = drawableOpacities && drawableIndex < drawableOpacities.length
        ? drawableOpacities[drawableIndex]
        : this._model.getDrawableOpacity(drawableIndex);
      if (!Number.isFinite(opacity) || opacity <= 0.001) {
        continue;
      }

      const vertexCount = this._model.getDrawableVertexCount(drawableIndex);
      const vertices = this._model.getDrawableVertices(drawableIndex);
      if (!vertices || vertexCount <= 0) {
        continue;
      }

      let drawableHasBounds = false;
      for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
        const offset = vertexIndex * 2;
        let x = vertices[offset];
        let y = vertices[offset + 1];
        if (useModelMatrix && this._modelMatrix) {
          x = this._modelMatrix.transformX(x);
          y = this._modelMatrix.transformY(y);
        }
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          continue;
        }

        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        visibleVertexCount++;
        drawableHasBounds = true;
      }

      if (drawableHasBounds) {
        visibleDrawableCount++;
      }
    }

    if (
      visibleDrawableCount <= 0 ||
      !Number.isFinite(minX) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxY)
    ) {
      return null;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    if (width <= 0 || height <= 0) {
      return null;
    }

    return {
      minX,
      maxX,
      minY,
      maxY,
      width,
      height,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      drawableCount: visibleDrawableCount,
      vertexCount: visibleVertexCount,
      coordinateSpace: useModelMatrix ? 'model_matrix' : 'model'
    };
  }

  private hiddenIdMatches(id: string, groups: HiddenModelGroup[]): boolean {
    return groups.some(group => {
      if ((group.partIds || []).indexOf(id) >= 0 || (group.drawableIds || []).indexOf(id) >= 0) {
        return true;
      }
      return (group.matchPrefixes || []).some(prefix => id.startsWith(prefix));
    });
  }

  private collectHiddenTargets(groups: HiddenModelGroup[]): HiddenTargetSet {
    const partIndices = new Set<number>();
    const drawableIndices = new Set<number>();
    const partCount = this._model.getPartCount();
    for (let index = 0; index < partCount; index++) {
      const id = this._model.getPartId(index).getString();
      if (!this.hiddenIdMatches(id, groups)) {
        continue;
      }
      partIndices.add(index);
      const childObjects = this._model.getPartChildDrawObjects(index);
      childObjects.drawableIndices.forEach(drawableIndex => drawableIndices.add(drawableIndex));
    }
    const drawableCount = this._model.getDrawableCount();
    for (let index = 0; index < drawableCount; index++) {
      const id = this._model.getDrawableId(index).getString();
      const parentPartIndex = this._model.getDrawableParentPartIndex(index);
      if (this.hiddenIdMatches(id, groups) || partIndices.has(parentPartIndex)) {
        drawableIndices.add(index);
      }
    }
    return { partIndices, drawableIndices };
  }

  private getCoreOpacityArrays(): { partOpacities?: Float32Array; drawableOpacities?: Float32Array } {
    const coreModel = this._model.getModel();
    return {
      partOpacities: coreModel?.parts?.opacities,
      drawableOpacities: coreModel?.drawables?.opacities
    };
  }

  private applyHiddenGroups(): void {
    if (!this._model) {
      return;
    }
    const wanted = this._hiddenGroups.length > 0
      ? this.collectHiddenTargets(this._hiddenGroups)
      : { partIndices: new Set<number>(), drawableIndices: new Set<number>() };
    const { partOpacities, drawableOpacities } = this.getCoreOpacityArrays();

    this._hiddenPartStates.forEach((state, index) => {
      if (wanted.partIndices.has(index)) {
        return;
      }
      if (partOpacities && index < partOpacities.length) {
        partOpacities[index] = state.opacity;
      }
      this._hiddenPartStates.delete(index);
    });

    this._hiddenDrawableStates.forEach((state, index) => {
      if (wanted.drawableIndices.has(index)) {
        return;
      }
      if (drawableOpacities && index < drawableOpacities.length) {
        drawableOpacities[index] = state.opacity;
      }
      this._model.setDrawableCulling(index, state.culling);
      this._model.setOverrideFlagForDrawableCullings(index, state.overridden);
      this._hiddenDrawableStates.delete(index);
    });

    wanted.partIndices.forEach(index => {
      if (!partOpacities || index >= partOpacities.length) {
        return;
      }
      if (!this._hiddenPartStates.has(index)) {
        this._hiddenPartStates.set(index, { opacity: partOpacities[index] });
      }
      partOpacities[index] = 0;
    });

    wanted.drawableIndices.forEach(index => {
      if (!this._hiddenDrawableStates.has(index)) {
        this._hiddenDrawableStates.set(index, {
          opacity: drawableOpacities && index < drawableOpacities.length ? drawableOpacities[index] : this._model.getDrawableOpacity(index),
          culling: this._model.getDrawableCulling(index),
          overridden: this._model.getOverrideFlagForDrawableCullings(index)
        });
      }
      if (drawableOpacities && index < drawableOpacities.length) {
        drawableOpacities[index] = 0;
      }
    });
  }

  private createFollowLookParameters(
    config: FollowConfig
  ): Array<LookParameterData> {
    return config.parameters.map(parameter => {
      const parameterId = CubismFramework.getIdManager().getId(
        parameter.parameterId
      );
      const factor = Number.isFinite(parameter.factor)
        ? parameter.factor
        : 0.0;

      return parameter.axis === 'X'
        ? new LookParameterData(parameterId, factor, 0.0, 0.0)
        : new LookParameterData(parameterId, 0.0, factor, 0.0);
    });
  }

  private setFollowTargetFromView(x: number, y: number): void {
    if (!this._modelMatrix) {
      this._followTargetX = x * followLookAmplitude;
      this._followTargetY = y * followLookAmplitude;
      return;
    }

    const centerX = this._modelMatrix.invertTransformX(0.0);
    const centerY = this._modelMatrix.invertTransformY(0.0);
    const localX = this._modelMatrix.invertTransformX(x);
    const localY = this._modelMatrix.invertTransformY(y);
    this._followTargetX = (localX - centerX) * followLookAmplitude;
    this._followTargetY = (localY - centerY) * followLookAmplitude;
  }

  private stopFollowExpression(): void {
    if (!this._followExpressionName) {
      return;
    }

    this._expressionManager?.stopAllMotions();
    this._followExpressionName = '';

    if (this._expressions.has('idle')) {
      this.setExpression('idle');
    }
  }

  private playFollowExpression(expressionName: string): void {
    this.stopFollowExpression();

    const requestedName = String(expressionName || '').trim();
    if (!requestedName) {
      return;
    }

    const baseName = requestedName.replace(/\.exp3$/i, '');
    const candidates = [requestedName, baseName, baseName + '.exp3'];
    const availableName = candidates.find(
      name => name && this._expressions.has(name)
    );
    if (!availableName) {
      return;
    }

    this._followExpressionName = availableName;
    this.setExpression(availableName);
  }

  public beginLegacyFollow(x: number, y: number): void {
    this.stopFollowExpression();
    this._activeFollowConfig = null;
    this._followReturning = false;
    this._followCurrentX = 0.0;
    this._followCurrentY = 0.0;
    this._followVelocityX = 0.0;
    this._followVelocityY = 0.0;
    this._legacyFollowActive = true;
    this._look?.setParameters(this._defaultLookParameters);
    this.setDragging(x, y);
  }

  public beginConfiguredFollow(
    config: FollowConfig,
    x: number,
    y: number
  ): void {
    const previousConfigId = this._activeFollowConfig?.id;
    this._legacyFollowActive = false;
    this.setDragging(0.0, 0.0);

    if (previousConfigId !== config.id) {
      this._followCurrentX = 0.0;
      this._followCurrentY = 0.0;
      this._followVelocityX = 0.0;
      this._followVelocityY = 0.0;
    }

    this._activeFollowConfig = config;
    this._followReturning = false;
    this.setFollowTargetFromView(x, y);
    this._look?.setParameters(this.createFollowLookParameters(config));
    this.playFollowExpression(config.expressionName);
  }

  public updateFollowDrag(x: number, y: number): void {
    if (this._activeFollowConfig) {
      this._followReturning = false;
      this.setFollowTargetFromView(x, y);
      return;
    }

    if (this._legacyFollowActive) {
      this.setDragging(x, y);
    }
  }

  public endFollowDrag(): FollowConfig | null {
    if (this._activeFollowConfig) {
      const endedConfig = this._activeFollowConfig;
      this._followTargetX = 0.0;
      this._followTargetY = 0.0;
      this._followReturning = true;
      this.stopFollowExpression();
      return endedConfig;
    }

    if (this._legacyFollowActive) {
      this._legacyFollowActive = false;
      this.setDragging(0.0, 0.0);
    }

    return null;
  }

  private updateLookParameters(
    model: CubismModel,
    deltaTimeSeconds: number
  ): void {
    if (!this._look) {
      return;
    }

    if (!this._activeFollowConfig) {
      this._dragManager.update(deltaTimeSeconds);
      this._look.updateParameters(
        model,
        this._dragManager.getX(),
        this._dragManager.getY()
      );
      return;
    }

    const damping = this._activeFollowConfig.damping > 0
      ? this._activeFollowConfig.damping
      : defaultFollowDamping;
    const deltaTime = Math.min(Math.max(deltaTimeSeconds, 0.0), 0.1);
    const nextX = smoothDamp(
      this._followCurrentX,
      this._followTargetX,
      this._followVelocityX,
      damping,
      deltaTime
    );
    const nextY = smoothDamp(
      this._followCurrentY,
      this._followTargetY,
      this._followVelocityY,
      damping,
      deltaTime
    );
    this._followCurrentX = nextX.value;
    this._followCurrentY = nextY.value;
    this._followVelocityX = nextX.velocity;
    this._followVelocityY = nextY.velocity;
    this._look.updateParameters(
      model,
      this._followCurrentX,
      this._followCurrentY
    );

    if (
      this._followReturning &&
      Math.abs(this._followCurrentX) <= followRestEpsilon &&
      Math.abs(this._followCurrentY) <= followRestEpsilon &&
      Math.abs(this._followVelocityX) <= followRestEpsilon &&
      Math.abs(this._followVelocityY) <= followRestEpsilon
    ) {
      this._followCurrentX = 0.0;
      this._followCurrentY = 0.0;
      this._followVelocityX = 0.0;
      this._followVelocityY = 0.0;
      this._followReturning = false;
      this._activeFollowConfig = null;
      this._look.setParameters(this._defaultLookParameters);
    }
  }

  public update(): void {
    if (this._state != LoadStep.CompleteSetup) return;

    const deltaTimeSeconds: number = LAppPal.getDeltaTime();
    this._userTimeSeconds += deltaTimeSeconds;

    //--------------------------------------------------------------------------
    this._model.loadParameters(); // 前回セーブされた状態をロード

    // Reset motion updated flag each frame
    this._motionUpdated = false;

    if (this._motionManager.isFinished()) {
      // モーションの再生がない場合、待機モーションの中からランダムで再生する
      this.startMotion(
        LAppDefine.MotionGroupIdle,
        0,
        LAppDefine.PriorityIdle
      );
    } else {
      this._motionUpdated = this._motionManager.updateMotion(
        this._model,
        deltaTimeSeconds
      ); // モーションを更新
    }

    if (!this._ambientMotionManager.isFinished()) {
      const ambientUpdated = this._ambientMotionManager.updateMotion(
        this._model,
        deltaTimeSeconds
      );
      this._motionUpdated = this._motionUpdated || ambientUpdated;
    }

    this._model.saveParameters(); // 状態を保存
    //--------------------------------------------------------------------------

    // UpdateSchedulerによる一括エフェクト更新
    this._updateScheduler.onLateUpdate(this._model, deltaTimeSeconds);

    this._model.update();
    this.applyHiddenGroups();
  }

  private startInitialIdleMotion(): void {
    if (this._hasStartedInitialMotion) {
      return;
    }

    if (this._modelSetting.getMotionCount(LAppDefine.MotionGroupIdle) <= 0) {
      return;
    }

    this._hasStartedInitialMotion = true;
    this.startMotion(LAppDefine.MotionGroupIdle, 0, LAppDefine.PriorityIdle);

    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('live2d-model-ready'));
    }, 420);
  }

  /**
   * 引数で指定したモーションの再生を開始する
   * @param group モーショングループ名
   * @param no グループ内の番号
   * @param priority 優先度
   * @param onFinishedMotionHandler モーション再生終了時に呼び出されるコールバック関数
   * @return 開始したモーションの識別番号を返す。個別のモーションが終了したか否かを判定するisFinished()の引数で使用する。開始できない時は[-1]
   */
  public startMotion(
    group: string,
    no: number,
    priority: number,
    onFinishedMotionHandler?: FinishedMotionCallback,
    onBeganMotionHandler?: BeganMotionCallback,
    nativeLoop: boolean = false
  ): CubismMotionQueueEntryHandle {
    if (priority == LAppDefine.PriorityForce) {
      this._motionManager.setReservePriority(priority);
    } else if (!this._motionManager.reserveMotion(priority)) {
      if (this._debugMode) {
        LAppPal.printMessage("[APP]can't start motion.");
      }
      return InvalidMotionQueueEntryHandleValue;
    }

    const motionFileName = this._modelSetting.getMotionFileName(group, no);

    // ex) idle_0
    const name = `${group}_${no}`;
    let motion: CubismMotion = this._motions.get(name) as CubismMotion;
    let autoDelete = false;

    if (motion == null) {
      fetch(`${this._modelHomeDir}${motionFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${motionFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          motion = this.loadMotion(
            arrayBuffer,
            arrayBuffer.byteLength,
            null,
            onFinishedMotionHandler,
            onBeganMotionHandler,
            this._modelSetting,
            group,
            no,
            this._motionConsistency
          );
        });

      if (motion) {
        motion.setEffectIds(this._eyeBlinkIds, this._lipSyncIds);
        autoDelete = true; // 終了時にメモリから削除
      } else {
        CubismLogError("Can't start motion {0} .", motionFileName);
        // ロードできなかったモーションのReservePriorityをリセットする
        this._motionManager.setReservePriority(LAppDefine.PriorityNone);
        return InvalidMotionQueueEntryHandleValue;
      }
    } else {
      motion.setBeganMotionHandler(onBeganMotionHandler);
      motion.setFinishedMotionHandler(onFinishedMotionHandler);
    }

    motion.setLoop(nativeLoop);
    if (nativeLoop) {
      motion.setLoopFadeIn(false);
    }

    //voice
    const voice = this._modelSetting.getMotionSoundFileName(group, no);
    if (voice.localeCompare('') != 0) {
      let path = voice;
      path = this._modelHomeDir + path;
      this._wavFileHandler.start(path);
    }

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]start motion: [${group}_${no}]`);
    }
    return this._motionManager.startMotionPriority(
      motion,
      autoDelete,
      priority
    );
  }

  public startAmbientMotion(
    group: string,
    no: number,
    nativeLoop: boolean = true
  ): boolean {
    const name = `${group}_${no}`;
    const motion = this._motions.get(name) as CubismMotion;
    if (!motion) {
      return false;
    }

    this._ambientMotionManager.stopAllMotions();
    this._ambientMotionManager.setReservePriority(LAppDefine.PriorityForce);
    motion.setBeganMotionHandler(undefined);
    motion.setFinishedMotionHandler(undefined);
    motion.setLoop(nativeLoop);
    if (nativeLoop) {
      motion.setLoopFadeIn(false);
    }

    const handle = this._ambientMotionManager.startMotionPriority(
      motion,
      false,
      LAppDefine.PriorityForce
    );
    return handle !== InvalidMotionQueueEntryHandleValue;
  }

  public stopAmbientMotion(): void {
    this._ambientMotionManager.stopAllMotions();
  }
  /**
   * ランダムに選ばれたモーションの再生を開始する。
   * @param group モーショングループ名
   * @param priority 優先度
   * @param onFinishedMotionHandler モーション再生終了時に呼び出されるコールバック関数
   * @return 開始したモーションの識別番号を返す。個別のモーションが終了したか否かを判定するisFinished()の引数で使用する。開始できない時は[-1]
   */
  public startRandomMotion(
    group: string,
    priority: number,
    onFinishedMotionHandler?: FinishedMotionCallback,
    onBeganMotionHandler?: BeganMotionCallback
  ): CubismMotionQueueEntryHandle {
    if (this._modelSetting.getMotionCount(group) == 0) {
      return InvalidMotionQueueEntryHandleValue;
    }

    const no: number = Math.floor(
      Math.random() * this._modelSetting.getMotionCount(group)
    );

    return this.startMotion(
      group,
      no,
      priority,
      onFinishedMotionHandler,
      onBeganMotionHandler
    );
  }

  /**
   * 引数で指定した表情モーションをセットする
   *
   * @param expressionId 表情モーションのID
   */
  public setExpression(expressionId: string): void {
    const motion: ACubismMotion = this._expressions.get(expressionId);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]expression: [${expressionId}]`);
    }

    if (motion != null) {
      this._expressionManager.startMotion(motion, false);
    } else {
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]expression[${expressionId}] is null`);
      }
    }
  }

  /**
   * ランダムに選ばれた表情モーションをセットする
   */
  public setRandomExpression(): void {
    if (this._expressions.size == 0) {
      return;
    }

    const no: number = Math.floor(Math.random() * this._expressions.size);

    for (let i = 0; i < this._expressions.size; i++) {
      if (i == no) {
        // const name: string = this._expressions._keyValues[i].first;
        const expressionsArray = [...this._expressions.entries()];
        const name: string = expressionsArray[i][0];
        this.setExpression(name);
        return;
      }
    }
  }

  /**
   * イベントの発火を受け取る
   */
  public motionEventFired(eventValue: string): void {
    CubismLogInfo('{0} is fired on LAppModel!!', eventValue);
  }

  /**
   * 当たり判定テスト
   * 指定ＩＤの頂点リストから矩形を計算し、座標をが矩形範囲内か判定する。
   *
   * @param hitArenaName  当たり判定をテストする対象のID
   * @param x             判定を行うX座標
   * @param y             判定を行うY座標
   */
  public hitTest(hitArenaName: string, x: number, y: number): boolean {
    // 透明時は当たり判定無し。
    if (this._opacity < 1) {
      return false;
    }

    const count: number = this._modelSetting.getHitAreasCount();

    for (let i = 0; i < count; i++) {
      if (this._modelSetting.getHitAreaName(i) == hitArenaName) {
        const drawId: CubismIdHandle = this._modelSetting.getHitAreaId(i);
        return this.isHit(drawId, x, y);
      }
    }

    return false;
  }

  /**
   * モーションデータをグループ名から一括でロードする。
   * モーションデータの名前は内部でModelSettingから取得する。
   *
   * @param group モーションデータのグループ名
   */
  public preLoadMotionGroup(group: string): void {
    for (let i = 0; i < this._modelSetting.getMotionCount(group); i++) {
      const motionFileName = this._modelSetting.getMotionFileName(group, i);

      // ex) idle_0
      const name = `${group}_${i}`;
      if (this._debugMode) {
        LAppPal.printMessage(
          `[APP]load motion: ${motionFileName} => [${name}]`
        );
      }

      fetch(`${this._modelHomeDir}${motionFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${motionFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          const tmpMotion: CubismMotion = this.loadMotion(
            arrayBuffer,
            arrayBuffer.byteLength,
            name,
            null,
            null,
            this._modelSetting,
            group,
            i,
            this._motionConsistency
          );

          if (tmpMotion != null) {
            tmpMotion.setEffectIds(this._eyeBlinkIds, this._lipSyncIds);

            if (this._motions.get(name) != null) {
              ACubismMotion.delete(this._motions.get(name));
            }

            this._motions.set(name, tmpMotion);

            this._motionCount++;
          } else {
            // loadMotionできなかった場合はモーションの総数がずれるので1つ減らす
            this._allMotionCount--;
          }

          if (this._motionCount >= this._allMotionCount) {
            this._state = LoadStep.LoadTexture;

            // 全てのモーションを停止する
            this._motionManager.stopAllMotions();

            this._updating = false;
            this._initialized = true;

            this.createRenderer(
              this._subdelegate.getCanvas().width,
              this._subdelegate.getCanvas().height
            );
            this.setupTextures();
            this.getRenderer().startUp(
              this._subdelegate.getGlManager().getGl()
            );
            this.getRenderer().loadShaders(LAppDefine.ShaderPath);
          }
        });
    }
  }

  /**
   * すべてのモーションデータを解放する。
   */
  public releaseMotions(): void {
    this._ambientMotionManager.stopAllMotions();
    this._motions.clear();
  }

  /**
   * 全ての表情データを解放する。
   */
  public releaseExpressions(): void {
    this._expressions.clear();
  }

  /**
   * モデルを描画する処理。モデルを描画する空間のView-Projection行列を渡す。
   */
  public doDraw(): void {
    if (this._model == null) return;

    // キャンバスサイズを渡す
    const canvas = this._subdelegate.getCanvas();
    const viewport: number[] = [0, 0, canvas.width, canvas.height];

    this.getRenderer().setRenderState(
      this._subdelegate.getFrameBuffer(),
      viewport
    );
    this.getRenderer().drawModel(LAppDefine.ShaderPath);
  }

  /**
   * モデルを描画する処理。モデルを描画する空間のView-Projection行列を渡す。
   */
  public draw(matrix: CubismMatrix44): void {
    if (this._model == null) {
      return;
    }

    // 各読み込み終了後
    if (this._state == LoadStep.CompleteSetup) {
      matrix.multiplyByMatrix(this._modelMatrix);

      this.getRenderer().setMvpMatrix(matrix);

      this.doDraw();
    }
  }

  public async hasMocConsistencyFromFile() {
    CSM_ASSERT(this._modelSetting.getModelFileName().localeCompare(``));

    // CubismModel
    if (this._modelSetting.getModelFileName() != '') {
      const modelFileName = this._modelSetting.getModelFileName();

      const response = await fetch(`${this._modelHomeDir}${modelFileName}`);
      const arrayBuffer = await response.arrayBuffer();

      this._consistency = CubismMoc.hasMocConsistency(arrayBuffer);

      if (!this._consistency) {
        CubismLogInfo('Inconsistent MOC3.');
      } else {
        CubismLogInfo('Consistent MOC3.');
      }

      return this._consistency;
    } else {
      LAppPal.printMessage('Model data does not exist.');
    }
  }

  public setSubdelegate(subdelegate: LAppSubdelegate): void {
    this._subdelegate = subdelegate;
  }

  /**
   * デストラクタに相当する処理のオーバーライド
   */
  public release(): void {
    if (this._look) {
      CubismLook.delete(this._look);
      this._look = null;
    }
    if (this._updateScheduler) {
      this._updateScheduler.release();
    }
    if (this._ambientMotionManager) {
      this._ambientMotionManager.release();
    }
    super.release();
  }

  /**
   * コンストラクタ
   */
  public constructor() {
    super();

    this._modelSetting = null;
    this._modelHomeDir = null;
    this._userTimeSeconds = 0.0;

    this._eyeBlinkIds = new Array<CubismIdHandle>();
    this._lipSyncIds = new Array<CubismIdHandle>();

    this._motions = new Map<string, ACubismMotion>();
    this._expressions = new Map<string, ACubismMotion>();

    this._hitArea = new Array<csmRect>();
    this._userArea = new Array<csmRect>();

    this._idParamAngleX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleX
    );
    this._idParamAngleY = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleY
    );
    this._idParamAngleZ = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleZ
    );
    this._idParamBodyAngleX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamBodyAngleX
    );

    if (LAppDefine.MOCConsistencyValidationEnable) {
      this._mocConsistency = true;
    }

    if (LAppDefine.MotionConsistencyValidationEnable) {
      this._motionConsistency = true;
    }

    this._state = LoadStep.LoadAssets;
    this._expressionCount = 0;
    this._textureCount = 0;
    this._motionCount = 0;
    this._allMotionCount = 0;
    this._wavFileHandler = new LAppWavFileHandler();
    this._consistency = false;
    this._look = null;
    this._defaultLookParameters = [];
    this._activeFollowConfig = null;
    this._legacyFollowActive = false;
    this._followReturning = false;
    this._followTargetX = 0.0;
    this._followTargetY = 0.0;
    this._followCurrentX = 0.0;
    this._followCurrentY = 0.0;
    this._followVelocityX = 0.0;
    this._followVelocityY = 0.0;
    this._followExpressionName = '';
    this._updateScheduler = new CubismUpdateScheduler();
    this._ambientMotionManager = new CubismMotionManager();
    this._motionUpdated = false;
    this._hasStartedInitialMotion = false;
    this._hiddenGroups = [];
    this._hiddenPartStates = new Map<number, HiddenPartState>();
    this._hiddenDrawableStates = new Map<number, HiddenDrawableState>();
  }

  private _updateScheduler: CubismUpdateScheduler; // アップデートスケジューラー
  private _ambientMotionManager: CubismMotionManager;
  private _motionUpdated: boolean; // モーション更新フラグ
  private _hasStartedInitialMotion: boolean;
  private _hiddenGroups: HiddenModelGroup[];
  private _hiddenPartStates: Map<number, HiddenPartState>;
  private _hiddenDrawableStates: Map<number, HiddenDrawableState>;
  private _subdelegate: LAppSubdelegate; // サブデリゲート

  _modelSetting: ICubismModelSetting; // モデルセッティング情報
  _modelHomeDir: string; // モデルセッティングが置かれたディレクトリ
  _userTimeSeconds: number; // デルタ時間の積算値[秒]

  _eyeBlinkIds: Array<CubismIdHandle>; // モデルに設定された瞬き機能用パラメータID
  _lipSyncIds: Array<CubismIdHandle>; // モデルに設定されたリップシンク機能用パラメータID

  _motions: Map<string, ACubismMotion>; // 読み込まれているモーションのリスト
  _expressions: Map<string, ACubismMotion>; // 読み込まれている表情のリスト

  _hitArea: Array<csmRect>;
  _userArea: Array<csmRect>;

  _idParamAngleX: CubismIdHandle; // パラメータID: ParamAngleX
  _idParamAngleY: CubismIdHandle; // パラメータID: ParamAngleY
  _idParamAngleZ: CubismIdHandle; // パラメータID: ParamAngleZ
  _idParamBodyAngleX: CubismIdHandle; // パラメータID: ParamBodyAngleX

  _look: CubismLook; // ドラッグ追従
  private _defaultLookParameters: Array<LookParameterData>;
  private _activeFollowConfig: FollowConfig | null;
  private _legacyFollowActive: boolean;
  private _followReturning: boolean;
  private _followTargetX: number;
  private _followTargetY: number;
  private _followCurrentX: number;
  private _followCurrentY: number;
  private _followVelocityX: number;
  private _followVelocityY: number;
  private _followExpressionName: string;

  _state: LoadStep; // 現在のステータス管理用
  _expressionCount: number; // 表情データカウント
  _textureCount: number; // テクスチャカウント
  _motionCount: number; // モーションデータカウント
  _allMotionCount: number; // モーション総数
  _wavFileHandler: LAppWavFileHandler; //wavファイルハンドラ
  _consistency: boolean; // MOC3整合性チェック管理用
}
